import { useEffect, useRef, useState } from 'react';
import { ArrowUpIcon, CheckIcon, PlusIcon } from 'lucide-react';
import type { AskUserToolInputDTO, AskUserToolOutputDTO } from '@shared/schemas/chat-ask-user';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  buildFreeTextWidgetOutput,
  buildWidgetAskUserOutput,
} from '../chat-ask-user-flow';

type AskUserWidgetProps = {
  allowFreeText?: boolean;
  allowMultiple?: boolean;
  hasContinuationError?: boolean;
  isSubmitting?: boolean;
  onRetry?: () => void;
  onSubmit: (output: AskUserToolOutputDTO) => void;
  options?: AskUserToolInputDTO['options'];
  output?: AskUserToolOutputDTO | null;
  question?: string;
  state?: 'input-available' | 'input-streaming' | 'output-available' | 'output-error';
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleChange = (event: MediaQueryListEvent) => {
      setReduced(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return reduced;
}

type OptionPillProps = {
  answered?: boolean;
  disabled: boolean;
  label: string;
  multiple: boolean;
  onClick: () => void;
  selected: boolean;
};

function OptionPill({
  disabled,
  label,
  multiple,
  onClick,
  selected,
  answered = false,
}: OptionPillProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-auto rounded-full border border-border/70 bg-transparent px-3.5 py-1.5 text-sm font-medium shadow-none transition-colors',
        !selected && !disabled && 'hover:border-border hover:bg-muted/20',
        selected && 'border-primary/35 bg-primary/[0.06] text-foreground hover:bg-primary/[0.08]',
        answered && selected && 'pointer-events-none',
      )}
    >
      {multiple ? (
        <span
          aria-hidden
          className={cn(
            'inline-flex size-3.5 shrink-0 items-center justify-center rounded-[4px] border',
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border/80 bg-background',
          )}
        >
          {selected ? <CheckIcon className="size-2.5" strokeWidth={3} /> : null}
        </span>
      ) : null}
      <span>{label}</span>
      {!multiple && selected ? (
        <CheckIcon className="size-3.5 text-primary/80" strokeWidth={2.5} aria-hidden />
      ) : null}
    </Button>
  );
}

type FreeTextRowProps = {
  disabled: boolean;
  onSubmit: (text: string) => void;
  reducedMotion: boolean;
};

function FreeTextRow({ disabled, onSubmit, reducedMotion }: FreeTextRowProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return (
      <Button
        type="button"
        variant="ghost"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="h-auto rounded-full border border-dashed border-border/60 bg-transparent px-3.5 py-1.5 text-sm font-normal text-muted-foreground shadow-none hover:border-border hover:bg-muted/15 hover:text-foreground"
      >
        <PlusIcon className="size-3.5" />
        Autre…
      </Button>
    );
  }

  return (
    <form
      className={cn(
        'flex w-full max-w-[24rem] items-center gap-2',
        !reducedMotion && 'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1',
      )}
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = value.trim();

        if (trimmed) {
          onSubmit(trimmed);
        }
      }}
    >
      <Input
        ref={inputRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Précisez votre réponse…"
        className="h-9 rounded-full border-border/60 bg-muted/15 px-3.5 text-sm shadow-none focus-visible:ring-ring/30"
      />
      <Button
        type="submit"
        size="icon-sm"
        disabled={!value.trim()}
        aria-label="Envoyer"
        className="rounded-full"
      >
        <ArrowUpIcon className="size-4" strokeWidth={2.5} />
      </Button>
    </form>
  );
}

function AskUserSkeleton() {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="h-3.5 w-[52%] animate-pulse rounded-md bg-muted/60" />
      <div className="flex flex-wrap gap-2">
        {[84, 60, 96].map((width) => (
          <div
            key={width}
            className="h-8 animate-pulse rounded-full bg-muted/50"
            style={{ width }}
          />
        ))}
      </div>
    </div>
  );
}

export function AskUserWidget({
  allowFreeText = false,
  allowMultiple = false,
  hasContinuationError = false,
  isSubmitting = false,
  onRetry,
  onSubmit,
  options = [],
  output = null,
  question = '',
  state = 'input-available',
}: AskUserWidgetProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [selected, setSelected] = useState<string[]>([]);
  const [locked, setLocked] = useState(false);
  const answered = state === 'output-available' || output !== null;
  const effectiveLocked = locked && !hasContinuationError;
  const disabled = answered || isSubmitting || effectiveLocked;

  const resolvedValues = answered ? (output?.values ?? selected) : selected;
  const resolvedFreeText = answered ? (output?.text ?? null) : null;

  const submitOutput = (nextOutput: AskUserToolOutputDTO) => {
    if (disabled) {
      return;
    }

    setLocked(true);
    onSubmit(nextOutput);
  };

  const pick = (value: string) => {
    if (disabled) {
      return;
    }

    if (allowMultiple) {
      setSelected((current) => (
        current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value]
      ));
      return;
    }

    setSelected([value]);
    submitOutput(buildWidgetAskUserOutput([value], options));
  };

  const confirmMulti = () => {
    if (!selected.length) {
      return;
    }

    submitOutput(buildWidgetAskUserOutput(selected, options));
  };

  const submitFreeText = (text: string) => {
    submitOutput(buildFreeTextWidgetOutput(text));
  };

  const visibleOptions = answered
    ? options.filter((option) => resolvedValues.includes(option.value))
    : options;

  return (
    <div
      role="group"
      aria-label={question || 'Question'}
      className={cn(
        'max-w-xl rounded-[var(--radius-surface-2xl)] border border-border/70 px-3.5 py-3',
        answered ? 'bg-muted/15' : 'bg-card/90',
      )}
    >
      {state === 'input-streaming' ? (
        <AskUserSkeleton />
      ) : (
        <>
          {question ? (
            <p
              className={cn(
                'mb-2.5 text-sm leading-snug',
                answered ? 'font-medium text-muted-foreground' : 'font-semibold text-foreground',
              )}
            >
              {question}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {visibleOptions.map((option) => (
              <OptionPill
                key={option.value}
                answered={answered}
                label={option.label}
                multiple={allowMultiple && !answered}
                selected={resolvedValues.includes(option.value)}
                disabled={disabled}
                onClick={() => pick(option.value)}
              />
            ))}

            {answered && resolvedFreeText ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/[0.06] px-3.5 py-1.5 text-sm font-medium text-foreground">
                {resolvedFreeText}
                <CheckIcon className="size-3.5 text-primary/80" strokeWidth={2.5} aria-hidden />
              </span>
            ) : null}

            {!answered && allowFreeText ? (
              <FreeTextRow
                disabled={disabled}
                onSubmit={submitFreeText}
                reducedMotion={reducedMotion}
              />
            ) : null}
          </div>

          {!answered && allowMultiple ? (
            <div className="mt-3">
              <Button
                type="button"
                size="sm"
                disabled={!selected.length || disabled}
                onClick={confirmMulti}
                className="h-8 rounded-full px-4 text-xs"
              >
                Confirmer{selected.length > 1 ? ` (${selected.length})` : ''}
              </Button>
            </div>
          ) : null}

          {hasContinuationError ? (
            <div className="mt-2.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-fit rounded-full border-border/70 px-3.5 text-xs shadow-none"
                onClick={onRetry}
              >
                Réessayer l’envoi
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
